"use client"

import { CAMPAIGNS } from "@/constants/admin-paths"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { t } from "@/languages/i18n"
import { CampaignFieldType } from "@/types/types"
import { createPath } from "@/utils/create-path"
import { extractIds } from "@/utils/extract-ids"
import { normalizeFormData } from "@/utils/normalize-form-data"
import { Form } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"
import { exportCampaign } from "../services/campaignService"
import { useCampaigns } from "./useCampaign"

export const useUpsertCampaign = () => {
  const [form] = Form.useForm()
  const router = useRouter()
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const isEditMode = !!id
  const [isFormReady, setIsFormReady] = useState(false)
  const [isFormChanged, setIsFormChanged] = useState(false)
  const [initialValues, setInitialValues] = useState<CampaignFieldType | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [status, setStatus] = useState("Draft")
  const subscriberObjectsRef = useRef<Array<{ id: number; fullname: string; phone: string }>>([])
  const postObjectsRef = useRef<Array<{ id: number; title: string }>>([])

  const { loading, actions } = useCampaigns()
  const { fetchCampaignByIdAndSetForm, handleUpsertCampaign } = actions

  const getNormalizedSnapshot = useCallback(() => {
    const values = form.getFieldsValue(true)

    return {
      ...normalizeFormData(values),
      posts: extractIds(values.posts),
      campaign_subscribers: extractIds(values.campaign_subscribers),
      tamdaStores: extractIds(values.tamdaStores),
      status,
    }
  }, [form, status])

  const {
    isChange,
    checkChange,
    modal,
    message,
    commitSnapshot,
    handleCancel: guardCancel,
  } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot,
  })

  useEffect(() => {
    checkChange()
  }, [status, checkChange])

  const loadData = useCallback(async () => {
    if (isEditMode && id) {
      const { campaign, formData } = await fetchCampaignByIdAndSetForm(Number(id), form)
      if (formData) {
        form.setFieldsValue(formData)
        setInitialValues(formData)
        setIsFormChanged(false)
        subscriberObjectsRef.current = (formData.campaign_subscribers as any[]) || []
        postObjectsRef.current = (formData.posts as any[]) || []
        // Set status based on publishedAt like other modules
        if (campaign?.attributes?.publishedAt) {
          setStatus("Published")
        } else {
          setStatus("Draft")
        }
        setIsFormReady(true)
      }
    }
  }, [id, isEditMode, fetchCampaignByIdAndSetForm, form])

  useEffect(() => {
    if (isEditMode) loadData()
    else {
      setIsFormReady(true)
    }
  }, [loadData, isEditMode])

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot()
      })
      return () => cancelAnimationFrame(animationId)
    }
  }, [isFormReady, commitSnapshot])

  const handleExportCsv = async () => {
    if (!id) return
    try {
      setExporting(true)
      const campaignName = form.getFieldValue("name")
      await exportCampaign(id, campaignName)
    } catch {
      message.error(t("Lỗi khi xuất file. Vui lòng thử lại."))
    } finally {
      setExporting(false)
    }
  }

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      const values = await form.validateFields()

      // Add publishedAt based on status like other modules
      const payload = {
        ...values,
        publishedAt: status === "Published" ? new Date().toISOString() : null,
      }

      const result = await handleUpsertCampaign(payload, id ? Number(id) : undefined)
      if (result.success) {
        if (isEditMode) {
          await loadData()
        } else {
          router.push(createPath(CAMPAIGNS.LIST.PATH, searchParams))
        }
        commitSnapshot()
      }
    } finally {
      setSubmitting(false)
    }
  }

  return {
    form,
    isEditMode,
    loading,
    submitting,
    exporting,
    status,
    setStatus,
    isButtonDisabled: isEditMode ? !isChange : false,
    handlers: {
      handleSubmit,
      handleExportCsv,
      handleBack: () => guardCancel(createPath(CAMPAIGNS.LIST.PATH, searchParams)),
      handleFormChange: checkChange,
    },
  }
}
